import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Video, Globe, Users, Award, Building2, Camera, 
  Play, CheckCircle, ArrowRight, ExternalLink
} from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C41E3A] font-bold mb-4">About Us</p>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6">
            The Story Behind <span className="text-[#C41E3A]">BrandStage</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
            Professional video profiles for businesses ready to stand out in the global marketplace. 
            Built on decades of trade expertise and production excellence.
          </p>
          <p className="text-zinc-500 text-sm mt-4">BrandStage is a brand of Continex Tradeline India Private Limited</p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-black tracking-tight mb-6">Our Mission</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                In a world where first impressions are made online, businesses need more than static profiles 
                and text descriptions. They need to show who they are, what they stand for, and the people 
                behind the brand.
              </p>
              <p className="text-zinc-400 leading-relaxed mb-4">
                BrandStage was launched in 2025 to give every business—from emerging exporters to established 
                enterprises—the tools to create compelling video-first profiles that build trust and 
                open doors.
              </p>
              <p className="text-zinc-300 leading-relaxed font-medium">
                We believe your story deserves to be seen, not just read.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
                <div className="text-4xl font-black text-[#C41E3A] mb-2">150+</div>
                <div className="text-zinc-500 text-sm">Trade Missions (Continex)</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
                <div className="text-4xl font-black text-[#C41E3A] mb-2">12</div>
                <div className="text-zinc-500 text-sm">Countries</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
                <div className="text-4xl font-black text-[#C41E3A] mb-2">18+</div>
                <div className="text-zinc-500 text-sm">Years (CINIMAGE)</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
                <div className="text-4xl font-black text-[#C41E3A] mb-2">48hr</div>
                <div className="text-zinc-500 text-sm">Typical Delivery*</div>
              </div>
            </div>
            <p className="text-zinc-600 text-xs mt-4 md:text-right">*Standard projects. Complex edits may vary.</p>
          </div>
        </div>
      </section>

      {/* Heritage Section - Two Pillars */}
      <section className="py-16 md:py-20 bg-zinc-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C41E3A] font-bold mb-4">Our Heritage</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Built on Two Pillars</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              BrandStage combines deep trade expertise with world-class video production capabilities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Continex */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-40 h-14 bg-white rounded-xl flex items-center justify-center p-2">
                  <img 
                    src="https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/jrun74lo_logo%202.png" 
                    alt="Continex Tradeline" 
                    className="h-full w-auto object-contain"
                  />
                </div>
                <p className="text-zinc-400 text-sm">Trade, Investment, B2B, G2B</p>
              </div>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Since 2007, Continex has been at the forefront of international trade, 
                organizing trade missions, connecting businesses across borders, and understanding 
                what it takes to succeed in global markets.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>150+ trade missions organized</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Operations across 12 countries</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Deep understanding of B2B markets</span>
                </li>
              </ul>
              <a 
                href="https://continex.in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#C41E3A] hover:underline font-medium"
              >
                Visit Continex <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* CINIMAGE */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-40 h-14 bg-white rounded-xl flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/glk2nwu8_Black%20and%20White%20Minimalist%20Studio%20Logo%20%281%29.webp" 
                    alt="CINIMAGE" 
                    className="w-auto object-contain scale-150"
                    style={{ height: '140%' }}
                  />
                </div>
                <p className="text-zinc-400 text-sm">Production Excellence</p>
              </div>
              <p className="text-zinc-400 leading-relaxed mb-6">
                With 18+ years of professional video production experience, CINIMAGE brings 
                broadcast-quality standards to every BrandStage production. From trade shows 
                to leadership interviews, our production team ensures your story is told right.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>18+ years production experience</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Broadcast-quality standards</span>
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Trade show & corporate specialists</span>
                </li>
              </ul>
              <a 
                href="https://www.cinimage.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-400 hover:underline font-medium"
              >
                Visit CINIMAGE <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Why This Matters</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              When trade expertise meets production excellence, businesses get profiles that actually work.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#C41E3A]/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#C41E3A]" />
              </div>
              <h3 className="font-bold text-lg mb-2">We Know Your Audience</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Years of trade missions mean we understand what international buyers, partners, 
                and investors look for when evaluating businesses.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <Video className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-bold text-lg mb-2">Professional Quality</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Broadcast-quality production ensures your videos reflect the professionalism 
                of your business—not a DIY afterthought.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="font-bold text-lg mb-2">Trust & Credibility</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Our combined heritage means BrandStage isn't a startup experiment—it's backed 
                by decades of real-world business experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-black tracking-tight mb-4">Ready to Tell Your Story?</h2>
          <p className="text-zinc-400 mb-8">
            Join businesses across the globe who trust BrandStage to showcase their leadership and build credibility.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-8 py-4 rounded-lg font-bold transition-colors"
            >
              Get Started <ArrowRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/#contact"
              className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-zinc-600 px-8 py-4 rounded-lg font-bold transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 border-t border-zinc-800">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <h4 className="font-bold text-zinc-300 mb-2">Registered Office</h4>
              <p className="text-zinc-500 text-sm">
                Continex Tradeline India Private Limited<br />
                Primus Building, No. SP-7A, 1st Floor<br />
                Guindy Industrial Estate<br />
                Chennai - 600032, Tamil Nadu, India
              </p>
            </div>
            <div>
              <h4 className="font-bold text-zinc-300 mb-2">Contact</h4>
              <p className="text-zinc-500 text-sm">
                <a href="mailto:operations@brandstage-media.com" className="text-[#C41E3A] hover:underline">
                  operations@brandstage-media.com
                </a><br />
                +91 9003035900
              </p>
            </div>
            <div>
              <h4 className="font-bold text-zinc-300 mb-2">Location</h4>
              <p className="text-zinc-500 text-sm">
                Bengaluru, India
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
